import { Alert, Button, Card, Form, Input, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type MapRow = {
  id: string;
  map_code: string;
  udise_field_code: string;
  title: string;
  source_entity: string;
  source_path: string;
  is_required: boolean;
  validation_rule: string;
  status: string;
  is_platform?: boolean;
};

export default function UdiseMapListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.udise.manage') || user?.permissions?.includes('education.admin')
  );
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ['education-udise-maps', q],
    queryFn: async () => {
      const res = await api.get<MapRow[]>('/education/udise/maps', { params: { q: q || undefined } });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post('/education/udise/maps', values),
    onSuccess: () => {
      message.success(t('udiseMapCreated'));
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['education-udise-maps'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const retire = useMutation({
    mutationFn: (id: string) => api.post(`/education/udise/maps/${id}/status`, { status: 'retired' }),
    onSuccess: () => {
      message.success(t('udiseMapUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-udise-maps'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('udiseMaps')}
      description={t('udiseMapsIntro')}
      extra={<Button onClick={() => navigate('/education/udise')}>{t('udise')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('udiseReadinessHint')} />
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 200 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
            {canManage ? (
              <Button style={{ marginLeft: 8 }} onClick={() => setShowForm((v) => !v)}>
                {t('newUdiseMap')}
              </Button>
            ) : null}
          </Form>
        </Card>
        {canManage && showForm ? (
          <Card size="small" title={t('newUdiseMap')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 560 }}
              initialValues={{ source_entity: 'student', value_type: 'string', validation_rule: 'none' }}
              onFinish={(values) => create.mutate(values)}
            >
              <Form.Item name="map_code" label={t('mapCode')} rules={[{ required: true }]}>
                <Input maxLength={40} />
              </Form.Item>
              <Form.Item name="udise_field_code" label={t('udiseFieldCode')} rules={[{ required: true }]}>
                <Input maxLength={60} />
              </Form.Item>
              <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="source_entity" label={t('sourceEntity')} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'student', label: 'student' },
                    { value: 'enrollment', label: 'enrollment' },
                    { value: 'campus', label: 'campus' },
                    { value: 'session', label: 'session' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="source_path" label={t('sourcePath')} rules={[{ required: true }]}>
                <Input maxLength={80} placeholder="pen_id" />
              </Form.Item>
              <Form.Item name="validation_rule" label={t('validationRule')}>
                <Select
                  options={[
                    { value: 'none', label: 'none' },
                    { value: 'required', label: 'required' },
                    { value: 'regex', label: 'regex' },
                    { value: 'enum', label: 'enum' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="validation_config" label={t('validationConfig')}>
                <Input maxLength={500} />
              </Form.Item>
              <Form.Item name="is_required" label={t('required')} valuePropName="checked">
                <Switch />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={create.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: t('mapCode'), dataIndex: 'map_code', width: 110 },
              { title: t('udiseFieldCode'), dataIndex: 'udise_field_code' },
              { title: t('title'), dataIndex: 'title' },
              {
                title: t('sourcePath'),
                render: (_: unknown, row: MapRow) => `${row.source_entity}.${row.source_path}`,
              },
              {
                title: t('required'),
                dataIndex: 'is_required',
                width: 90,
                render: (v: boolean) => (v ? t('yes') : t('no')),
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (v: string, row: MapRow) => (
                  <Space>
                    <Tag>{row.is_platform ? 'platform' : v}</Tag>
                    {canManage && !row.is_platform && v === 'active' ? (
                      <Button size="small" onClick={() => retire.mutate(row.id)}>
                        {t('retire')}
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
